<template>
  <UIInputSelect v-model="criteriaComparison" :size :data :disabled />
  <UIInputAutocomplete
    v-if="isFieldVisible"
    v-model="criteriaValue"
    :size
    :data="statusesList"
    :disabled
    @ready="el => el.focus()"
  />
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, ref, watch } from 'vue'

import {
  FilterComparison,
  FilterField,
  DropdownListData,
  TransactionRuleCriteria,
  InputSize,
} from '@types'

import useFiltersService from '@/services/filters'
import { getComparisonData } from '@/components/UI/Filter/components/utils/helpers'

import { UIInputAutocomplete, UIInputSelect } from '@ui/fields'

type Props = {
  disabled?: boolean
}

defineProps<Props>()

const modelValue = defineModel<TransactionRuleCriteria['params'][number]>({
  required: true,
})

const { fetchOptions } = useFiltersService()

const size = inject<InputSize>('size')

const statusesList = ref<DropdownListData[]>([])

const criteriaComparison = computed({
  get() {
    return modelValue.value.comparison
  },
  set(comparison) {
    modelValue.value = {
      ...modelValue.value,
      comparison,
    }
  },
})

const criteriaValue = computed({
  get() {
    return modelValue.value.value
  },
  set(value) {
    modelValue.value = {
      ...modelValue.value,
      value,
    }
  },
})

const data = computed(() =>
  getComparisonData({ isSimple: true } as FilterField).map(value => ({
    value,
    label: value,
  })),
)

const isFieldVisible = computed(
  () =>
    criteriaComparison.value &&
    ![FilterComparison.EMPTY, FilterComparison.NOTEMPTY].includes(
      criteriaComparison.value,
    ),
)

watch(isFieldVisible, value => {
  criteriaValue.value = value ? '' : undefined
})

onBeforeMount(async () => {
  if (!criteriaComparison.value) {
    criteriaComparison.value = FilterComparison.EQ
  }
  statusesList.value = await fetchOptions('LinkedDataTransactions', 'status')
})
</script>
